// react
import { Suspense, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

// tanstack
import { createFileRoute } from '@tanstack/react-router';

// material ui
import { Card, CardContent, Container, Grid, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

// api
import {
  type SummarizedSystemQoSOutput,
  useAllSystemQosAllSystemQosGetQuery,
} from '@/api/generated/energyAnalysis/endpoints/systemQos';
import {
  type ReadAccountDto,
  useAccountsGetCollectionAsyncByqueryOptionsQuery,
} from '@/api/generated/recalc/endpoints/v1/accounts';
import {
  type EnergyGroupState,
  type GetEnergyGroupDto,
  useGetEnergyGroupsQuery,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import PageHeader from '@/components/layout/PageHeader';

// feature-components
import EnergyGroupDrawerContainer from '@/features/service-manager/components/energyxplorer/deep-dive/EnergyGroupDrawerContainer';
import EnergyXplorerDatagrid from '@/features/service-manager/components/energyxplorer/deep-dive/EnergyXplorerDatagrid';

// components
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

type DeepDiveRow = Omit<GetEnergyGroupDto, 'energyGroupId' | 'accountId'> & {
  id: number;
  energyGroupId: number;
  accountId: number;
  AccountId: number;
  Account: string;
  hasService: boolean;
  nodeCount: number;
  initiativesCount: number;
  systemQoS: SummarizedSystemQoSOutput | null;
  QoS: number | null;
  ExecutionTime: string | null;
  OverallStatus: string | null;
  State: EnergyGroupState | null;
};

type StatCard = {
  name: string;
  value: number;
};

export const Route = createFileRoute(
  '/$accountId/_admin/operations/deep-dive/',
)({
  component: RouteComponent,
  staticData: { crumb: 'glossary:deepDive' },
});

function RouteComponent() {
  const [selectedRow, setSelectedRow] = useState<DeepDiveRow | null>(null);
  const selectedAccount = useSelector(getSelectedAccount);

  const { data: accounts = [], isFetching } =
    useAccountsGetCollectionAsyncByqueryOptionsQuery({
      orderby: 'AccountId',
      top: 10000,
    });
  const { data: energyGroups = [] } = useGetEnergyGroupsQuery(
    {},
    {
      pollingInterval: 60000,
    },
  );
  const { data: systemQoS = [] } = useAllSystemQosAllSystemQosGetQuery();

  const accountsById = useMemo(() => {
    return new Map<number, ReadAccountDto>(
      accounts.map((account) => [account.AccountId, account]),
    );
  }, [accounts]);

  const systemQoSByEnergyGroupId = useMemo(() => {
    return new Map<number, SummarizedSystemQoSOutput>(
      systemQoS.map((qos) => [qos.EnergyGroupId, qos]),
    );
  }, [systemQoS]);

  const rows = useMemo<DeepDiveRow[]>(() => {
    return energyGroups.flatMap((group) => {
      if (group.energyGroupId == null || group.accountId == null) {
        return [];
      }

      const account = accountsById.get(group.accountId);
      if (!account) {
        return [];
      }

      const qos = systemQoSByEnergyGroupId.get(group.energyGroupId) ?? null;

      return [
        {
          ...group,
          id: group.energyGroupId,
          energyGroupId: group.energyGroupId,
          accountId: group.accountId,
          AccountId: account.AccountId,
          Account: account.Name,
          hasService:
            account.Services?.some(
              (service) => service.Name === 'EnergyXplorer',
            ) ?? false,
          nodeCount: group.energyTreeNodes?.length ?? 0,
          initiativesCount: group.energyInitiatives?.length ?? 0,
          systemQoS: qos,
          QoS: qos?.SystemQoSScore ?? null,
          ExecutionTime: qos?.ExecutionTime ?? null,
          OverallStatus: qos?.OverallStatus ?? null,
          State: group.options?.state ?? null,
        },
      ];
    });
  }, [accountsById, energyGroups, systemQoSByEnergyGroupId]);

  const stats = useMemo<StatCard[]>(() => {
    if (energyGroups.length === 0) {
      return [];
    }

    const totalNodes = energyGroups.reduce((acc, g) => {
      return acc + (g.energyTreeNodes?.length ?? 0);
    }, 0);

    const totalInitiatives = energyGroups.reduce((acc, g) => {
      return acc + (g.energyInitiatives?.length ?? 0);
    }, 0);

    const totalEnergyGroups = energyGroups.length;

    const totalAccounts = new Set(
      energyGroups
        .map((group) => group.accountId)
        .filter((accountId): accountId is number => accountId != null),
    ).size;

    return [
      { name: 'Total Accounts', value: totalAccounts },
      { name: 'Total DeepDives', value: totalEnergyGroups },
      { name: 'Total Nodes', value: totalNodes },
      { name: 'Total Initiatives', value: totalInitiatives },
    ];
  }, [energyGroups]);

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
      <PageHeader title=" Operations / Deep Dive" disableArrow />
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              verticalAlign: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        }
      >
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {stats.map((s, index) => (
            <Grid
              key={index}
              size={{
                lg: 3
              }}>
              <ServiceCard {...s} />
            </Grid>
          ))}
        </Grid>
        <EnergyXplorerDatagrid
          initialRows={rows}
          isLoading={isFetching}
          setSelectedRow={setSelectedRow}
          account={selectedAccount}
        />
      </Suspense>
      <EnergyGroupDrawerContainer
        isOpen={Boolean(selectedRow?.Account)}
        onClose={() => setSelectedRow(null)}
        energyGroup={selectedRow}
      />
    </Container>
  );
}

type ServiceCardProps = {
  name: string;
  value: number;
};

const ServiceCard = ({ name, value }: ServiceCardProps) => {
  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            mb: 2,
          }}
        >
          <Typography variant="body1" color="textSecondary" fontWeight="bold">
            {name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
          >
            <Typography variant="h4" color="textPrimary" fontWeight={'bold'}>
              {value}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
